import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, MessageSquare, User, Download } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface CampaignMessage {
  _id: string;
  contactLead?: string;
  to?: string;
  status?: string;
  errorCode?: string;
  body?: string;
  clicks?: number;
}

function summarizeCampaignMessages(messages: CampaignMessage[]) {
  let delivered = 0;
  let undelivered = 0;
  let error = 0;
  let clicks = 0;

  for (const m of messages) {
    const s = (m.status ?? "").toLowerCase();
    const isUndelivered = s === "undelivered";
    const isError =
      s === "failed" ||
      (Boolean(m.errorCode) && !isUndelivered);
    const isDelivered = s === "delivered" || s === "read";

    if (isUndelivered) undelivered++;
    else if (isError) error++;
    else if (isDelivered) delivered++;

    if ((m.clicks ?? 0) > 0) clicks++;
  }

  return { sent: messages.length, delivered, undelivered, error, clicks };
}

export function MessagesSMSCampaignMessages() {
  const { id } = useParams<{ id: string }>();
  const [campaignName, setCampaignName] = useState<string>("");
  const [campaignWorkerStatus, setCampaignWorkerStatus] = useState<
    string | undefined
  >();
  const [campaignTrackLinks, setCampaignTrackLinks] = useState(false);
  const [messages, setMessages] = useState<CampaignMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchData() {
      if (!id) return;
      setIsLoading(true);
      try {
        const [campaignRes, messagesRes] = await Promise.all([
          fetch(`/api/campaigns/${id}`),
          fetch(`/api/campaigns/${id}/sms-messages`),
        ]);

        if (campaignRes.ok) {
          const data = await campaignRes.json();
          setCampaignName(data.campaign?.name || "");
          setCampaignWorkerStatus(data.campaign?.workerStatus);
          setCampaignTrackLinks(Boolean(data.campaign?.trackLinks));
        }

        if (messagesRes.ok) {
          const data = await messagesRes.json();
          setMessages(data.messages || []);
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error("Failed to fetch campaign messages:", error);
        setMessages([]);
      } finally {
        setIsLoading(false);
      }
    }

    void fetchData();
    return () => clearBreadcrumbs();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setBreadcrumbs([
      { label: "Messages", path: "/messages" },
      { label: "SMS", path: "/messages/sms/campaigns" },
      { label: "Campaigns", path: "/messages/sms/campaigns" },
      {
        label: campaignName || "Campaign",
        path: `/messages/sms/campaigns/${id}/messages`,
      },
    ]);
  }, [id, campaignName]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  const { sent, delivered, undelivered, error: errorCount, clicks } =
    summarizeCampaignMessages(messages);

  const canExportReport =
    campaignWorkerStatus !== "processing" && messages.length > 0;

  const handleExportCsv = async () => {
    if (!id || !canExportReport) return;
    setIsExporting(true);
    try {
      const response = await fetch(`/api/campaigns/${id}/sms-messages/export`);
      if (!response.ok) {
        console.error("Failed to export SMS campaign report");
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disposition = response.headers.get("Content-Disposition");
      const filenameMatch = disposition?.match(/filename="([^"]+)"/);
      a.download =
        filenameMatch?.[1] ??
        `sms-campaign-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export SMS campaign report:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto w-full">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">
          {campaignName ? `${campaignName} — Messages` : "Campaign messages"}
        </h1>
        {canExportReport ? (
          <Button
            type="button"
            variant="secondary"
            disabled={isExporting}
            onClick={() => void handleExportCsv()}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export CSV
          </Button>
        ) : null}
      </div>

      {campaignWorkerStatus === "processing" ? (
        <div
          role="status"
          className="mb-6 flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sky-950 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50"
        >
          <Loader2
            className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-sky-600 dark:text-sky-400"
            aria-hidden
          />
          <p className="text-sm leading-relaxed">
            This campaign is sending now.
          </p>
        </div>
      ) : null}

      <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${campaignTrackLinks ? "lg:grid-cols-5" : "lg:grid-cols-4"} mb-6`}>
        <Card className="py-4 gap-0 shadow-sm">
          <CardContent className="space-y-1 pt-0">
            <p className="text-sm text-muted-foreground">Sent</p>
            <p className="text-2xl font-semibold tabular-nums">{sent}</p>
          </CardContent>
        </Card>
        <Card className="py-4 gap-0 shadow-sm">
          <CardContent className="space-y-1 pt-0">
            <p className="text-sm text-muted-foreground">Delivered</p>
            <p className="text-2xl font-semibold tabular-nums">{delivered}</p>
          </CardContent>
        </Card>
        <Card className="py-4 gap-0 shadow-sm">
          <CardContent className="space-y-1 pt-0">
            <p className="text-sm text-muted-foreground">Undelivered</p>
            <p className="text-2xl font-semibold tabular-nums">
              {undelivered}
            </p>
          </CardContent>
        </Card>
        <Card className="py-4 gap-0 shadow-sm">
          <CardContent className="space-y-1 pt-0">
            <p className="text-sm text-muted-foreground">Error</p>
            <p className="text-2xl font-semibold tabular-nums">{errorCount}</p>
          </CardContent>
        </Card>
        {campaignTrackLinks && (
          <Card className="py-4 gap-0 shadow-sm">
            <CardContent className="space-y-1 pt-0">
              <p className="text-sm text-muted-foreground">Clicks</p>
              <p className="text-2xl font-semibold tabular-nums">{clicks}</p>
            </CardContent>
          </Card>
        )}
      </div>

      <TooltipProvider delayDuration={200}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Contact</TableHead>
              <TableHead className="w-[72px] text-center">Message</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Error</TableHead>
              {campaignTrackLinks && <TableHead className="text-center">Clicks</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {messages.length > 0 ? (
              messages.map((m) => {
                const leadId = m.contactLead ? String(m.contactLead) : null;
                return (
                  <TableRow key={m._id}>
                    <TableCell>
                      {leadId ? (
                        <Link
                          to={`/contacts/${leadId}/edit`}
                          className="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-muted"
                          aria-label="Open contact"
                        >
                          <User className="h-4 w-4" />
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-muted"
                            aria-label="Message body"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="left"
                          className="max-w-sm whitespace-pre-wrap text-left"
                        >
                          {m.body ?? ""}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {m.to ? (
                        <Link
                          to={`/messages/sms/inbox/${encodeURIComponent(m.to)}`}
                          className="text-primary underline underline-offset-2 decoration-primary"
                        >
                          {m.to}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="capitalize">
                      {m.status ?? "—"}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {m.errorCode ?? "—"}
                    </TableCell>
                    {campaignTrackLinks && (
                      <TableCell className="text-center tabular-nums">
                        {m.clicks ?? 0}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={campaignTrackLinks ? 6 : 5} className="h-24 text-center">
                  No messages for this campaign.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TooltipProvider>
    </div>
  );
}
